<template>
  <div class="stock-count-page" @click="keepBarcodeFocus">

    <!-- Header fijo -->
    <div class="stock-count-header sticky-top text-white p-3">
      <b-row align-v="center">
        <b-col cols="2">
          <b-button variant="link" @click="goBack()" class="text-white p-0">
            <AppIcon name="back" font-scale="1.5" />
          </b-button>
        </b-col>
        <b-col cols="8" class="text-center">
          <h5 class="mb-0">Recuento de Stock</h5>
          <small class="text-muted">Visualización y ajustes de stock por depósito</small>
        </b-col>
        <b-col cols="2" class="text-right">
          <b-button
            variant="link"
            @click="toggleFocusMode()"
            class="text-white p-0"
            :title="focusMode ? 'Salir de pantalla completa' : 'Usar pantalla completa'"
          >
            <AppIcon name="arrows-fullscreen" font-scale="1.2" />
          </b-button>
        </b-col>
      </b-row>
    </div>

    <div class="p-3 pb-5">

      <!-- Búsqueda -->
      <div v-if="!product">
        <b-card class="mb-3 shadow-sm">
          <div style="position:relative">
            <b-input-group size="lg">
              <b-input-group-prepend>
                <b-button
                  :variant="searchMode === 'barcode' ? 'primary' : 'info'"
                  :title="searchMode === 'barcode' ? 'Cambiar a búsqueda manual' : 'Cambiar a escaneo por código de barras'"
                  @click="switchSearchMode(searchMode === 'barcode' ? 'manual' : 'barcode')"
                >
                  <AppIcon :name="searchMode === 'barcode' ? 'upc-scan' : 'search'" />
                </b-button>
              </b-input-group-prepend>

              <b-form-input
                v-if="searchMode === 'barcode'"
                ref="barcodeInput"
                v-model="barcode"
                type="text"
                placeholder="Esperando lector..."
                :disabled="loading.search"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                :inputmode="isMobileLayout ? 'text' : 'none'"
              />
              <b-form-input
                v-else
                ref="manualSearchInput"
                v-model="manualSearch.query"
                placeholder="Nombre, código, categoría o subcategoría..."
                :disabled="loading.search"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                @keydown.down.prevent="manualSearchMoveDown"
                @keydown.up.prevent="manualSearchMoveUp"
                @keydown.enter.prevent="manualSearchConfirm"
                @focus="manualSearchFocus"
                @blur="manualSearchBlur"
              />

              <b-input-group-append v-if="searchMode === 'barcode'">
                <b-button variant="outline-secondary" @click="startCamera()" :disabled="loading.search" title="Escanear con cámara">
                  <AppIcon name="camera" />
                </b-button>
              </b-input-group-append>
              <b-input-group-append v-else-if="manualSearch.loading">
                <b-button variant="outline-secondary" disabled>
                  <b-spinner small></b-spinner>
                </b-button>
              </b-input-group-append>
            </b-input-group>

            <!-- Dropdown manual -->
            <div
              v-if="searchMode === 'manual' && manualSearch.show"
              ref="manualDropdown"
              class="sc-manual-dropdown"
            >
              <div
                v-for="(item, idx) in manualSearch.results"
                :key="item.id"
                class="sc-manual-item"
                :class="{ 'sc-manual-item--active': manualSearch.activeIdx === idx }"
                @mousedown.prevent="selectManualResult(item)"
                @touchstart="onDropdownItemTouchStart"
                @touchend.prevent="onDropdownItemTouchEnd(item, $event)"
              >
                <div class="sc-manual-item__name">{{ item.name }}</div>
                <div class="sc-manual-item__meta">
                  <code v-if="item.code" class="sc-manual-item__code">{{ item.code }}</code>
                  <code v-if="item.barcode && item.barcode !== item.code" class="sc-manual-item__code">
                    <AppIcon name="upc-scan" class="mr-1" />{{ item.barcode }}
                  </code>
                  <span v-if="item.category" class="sc-manual-item__cat">
                    {{ item.category }}<template v-if="item.subcategory"> / {{ item.subcategory }}</template>
                  </span>
                </div>
              </div>
              <div v-if="manualSearch.results.length === 0" class="sc-manual-item text-muted">
                <em>Sin resultados para "{{ manualSearch.query }}"</em>
              </div>
            </div>
          </div>

          <small v-if="searchMode === 'barcode'" class="text-muted mt-2 d-block">
            Apuntá el lector al código de barras — el resultado aparece automáticamente
          </small>
          <small v-else class="text-muted mt-2 d-block">
            Buscá por nombre, código, categoría o subcategoría
          </small>

          <div v-if="searchError" class="mt-2">
            <b-alert variant="danger" show class="mb-0 py-2">
              <AppIcon name="exclamation-circle" class="mr-1" />{{ searchError }}
            </b-alert>
          </div>
        </b-card>

        <EmptyState
          icon="box-seam"
          title="Preparado para un nuevo recuento"
          description="El sistema calculará los ajustes al comparar el stock actual con el real ingresado."
          class="py-4" />
      </div>

      <!-- Resultado: producto + stock por depósito -->
      <div v-if="product">

        <!-- Card del producto -->
        <b-card class="mb-3 shadow-sm border-primary">
          <div class="d-flex align-items-start justify-content-between">
            <div class="flex-grow-1 mr-3">
              <h6 class="mb-1 font-weight-bold">{{ product.name }}</h6>
              <div class="text-muted small">
                <span v-if="product.barcode">Código de Barras: <strong>{{ product.barcode }}</strong></span>
                <span v-else-if="product.code">Código: <strong>{{ product.code }}</strong></span>
              </div>
            </div>
            <div class="d-flex align-items-center">
              <b-button
                variant="outline-secondary"
                size="sm"
                class="stock-count-reset-btn flex-shrink-0"
                @click="resetSearch()"
                title="Buscar otro producto"
              >
                <AppIcon name="upc-scan" :class="isMobileLayout ? '' : 'mr-1'" />
                <span class="d-none d-sm-inline">Buscar otro</span>
              </b-button>
            </div>
          </div>
        </b-card>

        <!-- Sin cambios -->
        <div v-if="product && changedCount === 0 && !hints.stockTip" class="stock-count-empty-state mt-3 mb-3">
          <AppIcon name="pencil-square" class="stock-count-empty-icon" />
          <div class="flex-grow-1">
            <div class="stock-count-empty-title">Cargá el stock real del recuento</div>
            <div class="stock-count-empty-text">
              Ingresá la cantidad real de cada depósito. Si hay diferencias, el sistema prepara automáticamente los ajustes de ingreso o baja.
            </div>
          </div>
          <b-button variant="link" class="stock-count-hint-close p-0 ml-2" title="No mostrar de nuevo" @click="dismissHint('stockTip')">
            <AppIcon name="x" font-scale="1.4" />
          </b-button>
        </div>

        <b-card v-if="unitMeasurementOptions.length > 1" class="mb-3 shadow-sm">
          <b-form-group label="Ingresar cantidades en" label-class="small text-muted mb-1" class="mb-0">
            <b-form-select
              v-model="selectedUnitMeasurementId"
              :options="unitMeasurementOptions"
              :disabled="unitMeasurementOptions.length <= 1 || changedCount > 0"
              @change="onUnitMeasurementChange()"
            ></b-form-select>
          </b-form-group>
        </b-card>

        <!-- Stock por depósito -->
        <h6 class="text-uppercase text-muted mb-2" style="font-size:1rem;letter-spacing:1px;">
          <AppIcon name="building" class="mr-1" />Stock por depósito
        </h6>

        <div v-if="stockByDeposit.length === 0" class="text-center text-muted py-3">
          <p class="small">No hay depósitos activos configurados</p>
        </div>

        <b-card
          v-for="(item, index) in stockByDeposit"
          :key="item.deposit.id"
          class="mb-2 shadow-sm"
          :class="{ 'border-warning': hasChanged(item), 'border-success': item.real_quantity > item.current_quantity && hasChanged(item), 'border-danger': item.real_quantity < item.current_quantity && hasChanged(item) }"
        >
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong class="text-dark">{{ item.deposit.name }}</strong>
            <span class="text-muted small">
              Stock actual: <strong>{{ formatQty(item.current_quantity) }}</strong>
              <span v-if="selectedUnitMeasurement && selectedUnitMeasurement.reference"> {{ selectedUnitMeasurement.reference }}</span>
            </span>
          </div>

          <b-input-group size="sm">
            <b-input-group-prepend is-text>
              <span class="small text-muted">Stock Real</span>
            </b-input-group-prepend>
            <formatted-number-input
              v-model="stockByDeposit[index].real_quantity"
              :input-class="getInputClass(item)"
              @input="onQuantityChange(item)"
            ></formatted-number-input>
            <b-input-group-append v-if="selectedUnitMeasurement && selectedUnitMeasurement.reference" is-text>
              <span class="small text-muted">{{ selectedUnitMeasurement.reference }}</span>
            </b-input-group-append>
          </b-input-group>

          <div v-if="hasChanged(item)" class="mt-1">
            <span :class="['diff-label', getDiffClass(item)]">
              <AppIcon :name="item.real_quantity > item.current_quantity ? 'arrow-up-circle-fill' : 'arrow-down'" class="mr-1" />
              {{ item.real_quantity > item.current_quantity ? 'Ajuste de ingreso' : 'Ajuste de baja' }}:
              <strong>{{ formatQty(Math.abs(item.real_quantity - item.current_quantity)) }}</strong>
            </span>
          </div>
        </b-card>

        <b-card v-if="stockByDeposit.length > 1" class="stock-count-total-card mt-3 mb-3">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div class="stock-count-total-title">Total del recuento</div>
              <div class="stock-count-total-subtitle">Suma de todos los depósitos</div>
            </div>
            <b-badge v-if="selectedUnitMeasurement && selectedUnitMeasurement.reference" variant="light" class="stock-count-total-unit">
              {{ selectedUnitMeasurement.reference }}
            </b-badge>
          </div>

          <div class="stock-count-total-grid">
            <div class="stock-count-total-box">
              <div class="stock-count-total-label">Stock del sistema</div>
              <div class="stock-count-total-value">{{ formatQty(totalCurrentQuantity) }}</div>
            </div>
            <div class="stock-count-total-box stock-count-total-box-primary">
              <div class="stock-count-total-label">Stock recuento</div>
              <div class="stock-count-total-value">{{ formatQty(totalRealQuantity) }}</div>
            </div>
          </div>

          <div v-if="hasTotalChanged" class="stock-count-total-diff" :class="totalDiffQuantity > 0 ? 'text-success' : 'text-danger'">
            <AppIcon :name="totalDiffQuantity > 0 ? 'arrow-up' : 'arrow-down'" class="mr-1" />
            Diferencia total: {{ formatQty(Math.abs(totalDiffQuantity)) }}
          </div>
        </b-card>

      </div>

    </div>

    <div v-if="product" class="fixed-bottom mobile-footer border-top p-3">
      <b-button
        block
        variant="success"
        size="lg"
        :disabled="changedCount === 0 || loading.process"
        @click="processAdjustments()">
        <b-spinner small v-if="loading.process" class="mr-1"></b-spinner>
        <AppIcon v-else name="check2-circle" class="mr-1" />
        Procesar ajustes ({{ changedCount }})
      </b-button>
    </div>

    <!-- Modal cámara -->
    <b-modal
      v-model="modal.camera"
      hide-footer
      centered
      @hide="stopCamera()"
    >
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="camera" /></span>
        <h5 class="app-modal-header__title">Escanear código</h5>
      </div>
      <div class="text-center">
        <!-- Quagga necesita un contenedor (div) como target: inserta su propio video/canvas adentro -->
        <div ref="cameraContainer" class="camera-preview-container">
          <video v-show="cameraEngine !== 'quagga'" ref="videoElement" autoplay muted playsinline webkit-playsinline class="camera-preview"></video>
        </div>
        <p class="text-muted small mt-2 mb-0">Apuntá la cámara al código de barras</p>
      </div>
    </b-modal>

    <!-- Modal éxito -->
    <b-modal
      v-model="modal.success"
      hide-footer
      centered
    >
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="check2-circle" /></span>
        <h5 class="app-modal-header__title">Ajustes procesados</h5>
      </div>
      <div class="text-center py-2">
        <AppIcon name="check2-circle" font-scale="3" class="text-success mb-3" />
        <p class="mb-1">Se procesaron <strong>{{ lastResult.operations_created }}</strong> operaciones de ajuste</p>
        <p class="text-muted small mb-0">El stock ha sido actualizado correctamente</p>
        <b-button variant="primary" block size="lg" class="mt-3" @click="modal.success = false; resetSearch()">
          <AppIcon name="refresh" class="mr-1" />Nuevo recuento
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import serviceAPI from './services'
import Error from '@/handler/error'
import Profiles from '@/config/profiles'
import Modules from '@/config/modules'
import Helper from '@/handler/helper'
import Storage from '@/handler/storageLocal'

const FormattedNumberInput = {
  props: {
    value: { default: 0 },
    disabled: { type: Boolean, default: false },
    size: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    inputClass: { type: String, default: '' },
  },
  data() { return { displayValue: '', focused: false } },
  watch: {
    value(val) { if (!this.focused) this.displayValue = this.fmt(val) }
  },
  created() { this.displayValue = this.fmt(this.value) },
  methods: {
    fmt(val) {
      const num = parseFloat(val)
      if (isNaN(num)) return ''
      return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 8 }).format(num)
    },
    parse(str) {
      const cleaned = String(str || '').replace(/\./g, '').replace(',', '.')
      const num = parseFloat(cleaned)
      return isNaN(num) ? 0 : num
    },
    onFocus(e) {
      this.focused = true
      const el = e.target
      const num = parseFloat(this.value)
      const raw = isNaN(num) || num === 0 ? '' : String(num).replace('.', ',')
      el.value = raw; this.displayValue = raw
      this.$nextTick(() => el.select())
    },
    onInput(e) { this.displayValue = e.target.value; this.$emit('input', this.parse(e.target.value)) },
    onKeydown(e) {
      const allowed = ['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End']
      if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return
      if (/^\d$/.test(e.key)) return
      if ((e.key === ',' || e.key === '.') && !e.target.value.includes(',')) {
        if (e.key === '.') {
          e.preventDefault()
          const p = e.target.selectionStart, v = e.target.value
          const nv = v.slice(0, p) + ',' + v.slice(e.target.selectionEnd)
          e.target.value = nv; e.target.setSelectionRange(p + 1, p + 1); this.displayValue = nv
        }
        return
      }
      if (e.key === '-' && e.target.selectionStart === 0 && !e.target.value.includes('-')) return
      e.preventDefault()
    },
    onBlur(e) {
      this.focused = false
      const num = this.parse(e.target.value)
      this.$emit('input', num); this.displayValue = this.fmt(num); this.$emit('blur', e)
    },
  },
  render(h) {
    const classes = ['form-control']
    if (this.size) classes.push(`form-control-${this.size}`)
    if (this.inputClass) classes.push(this.inputClass)
    return h('input', {
      domProps: { value: this.displayValue, disabled: this.disabled },
      class: classes,
      attrs: { type: 'text', inputmode: 'decimal', placeholder: this.placeholder,
               autocomplete: 'off', autocorrect: 'off', autocapitalize: 'off', spellcheck: 'false' },
      on: { focus: this.onFocus, input: this.onInput, keydown: this.onKeydown, blur: this.onBlur }
    })
  }
}

export default {
  components: { FormattedNumberInput },
  data: () => {
    return {
      access: {
        module_id: Modules.DEPOSITOS,
        profile_id: Profiles.PERSONAL,
        view_reference: 'crudDepositsStockCount',
        elements: {}
      },
      hints: { stockTip: false },
      searchMode: 'barcode',
      barcode: '',
      manualSearch: { query: '', results: [], loading: false, show: false, activeIdx: -1, focusTarget: 'input', _timer: null },
      isMobileLayout: false,
      product: null,
      unitMeasurement: null,
      unitMeasurements: [],
      selectedUnitMeasurementId: null,
      stockByDeposit: [],
      searchError: null,
      loading: {
        search: false,
        process: false,
      },
      modal: {
        success: false,
        camera: false,
      },
      cameraStream: null,
      cameraDetector: null,
      cameraInterval: null,
      quaggaInstance: null,
      cameraEngine: null,
      cameraSupported: true,
      focusMode: false,
      lastResult: {
        operations_created: 0,
      },
    }
  },
  computed: {
    changedCount() {
      return this.stockByDeposit.filter(item => this.hasChanged(item)).length
    },
    totalCurrentQuantity() {
      return this.stockByDeposit.reduce((total, item) => total + this.toNumber(item.current_quantity), 0)
    },
    totalRealQuantity() {
      return this.stockByDeposit.reduce((total, item) => total + this.toNumber(item.real_quantity), 0)
    },
    totalDiffQuantity() {
      return this.totalRealQuantity - this.totalCurrentQuantity
    },
    hasTotalChanged() {
      return Math.abs(this.totalDiffQuantity) >= 0.00005
    },
    selectedUnitMeasurement() {
      return this.unitMeasurements.find(item => item.id == this.selectedUnitMeasurementId) || this.unitMeasurement
    },
    unitMeasurementOptions() {
      return this.unitMeasurements.map(item => ({
        value: item.id,
        text: this.getUnitMeasurementOptionText(item)
      }))
    }
  },
  watch: {
    'manualSearch.query'() {
      this.manualSearch.focusTarget = 'input'
      this.manualSearch.activeIdx = -1
    },
    barcode(val) {
      if (!val) return
      if (this.searchMode !== 'barcode') return
      clearTimeout(this._barcodeTimer)
      this._barcodeTimer = setTimeout(() => {
        if (this.barcode && !this.product && !this.loading.search && this.searchMode === 'barcode') {
          this.searchProduct()
        }
      }, 180)
    },
  },
  beforeDestroy() {
    this.stopCamera()
    this.exitBrowserFullscreen()
    this.disableFocusMode()
    window.removeEventListener('resize', this.updateMobileLayout)
  },
  deactivated() {
    this.stopCamera()
    this.exitBrowserFullscreen()
    this.disableFocusMode()
  },
  created() {
    Helper.hasAccessView(this.access)
    const saved = localStorage.getItem('sc_search_mode')
    if (saved === 'manual') this.searchMode = 'manual'
    try {
      const hints = JSON.parse(localStorage.getItem('sc_hints') || '{}')
      if (hints.stockTip) this.hints.stockTip = true
    } catch (_) { /* localStorage unavailable */ }
  },
  mounted() {
    this.disableFocusMode()
    this.updateMobileLayout()
    window.addEventListener('resize', this.updateMobileLayout)
    this.focusSearch()
  },
  methods: {
    goBack() {
      this.exitBrowserFullscreen()
      this.disableFocusMode()
      this.$router.push({ name: 'DepositsStaffDashboard' })
    },
    async toggleFocusMode() {
      const enable = !this.focusMode
      if (enable) {
        this.enableFocusMode()
        await this.requestBrowserFullscreen()
      } else {
        await this.exitBrowserFullscreen()
        this.disableFocusMode()
      }
    },
    enableFocusMode() {
      this.focusMode = true
      if (typeof document === 'undefined') return
      if (document.body) {
        document.body.classList.add('stock-count-focus-mode')
      }
    },
    disableFocusMode() {
      this.focusMode = false
      if (typeof document === 'undefined') return
      if (document.body) {
        document.body.classList.remove('stock-count-focus-mode')
      }
    },
    async requestBrowserFullscreen() {
      if (typeof document === 'undefined') return
      if (document.fullscreenElement) return
      const target = document.documentElement
      if (target && typeof target.requestFullscreen === 'function') {
        try {
          await target.requestFullscreen()
        } catch (error) {
          if (error) return false
        }
      }
    },
    async exitBrowserFullscreen() {
      if (typeof document === 'undefined') return
      if (!document.fullscreenElement) return
      if (typeof document.exitFullscreen === 'function') {
        try {
          await document.exitFullscreen()
        } catch (error) {
          if (error) return false
        }
      }
    },
    dismissHint(key) {
      this.hints[key] = true
      try {
        const hints = JSON.parse(localStorage.getItem('sc_hints') || '{}')
        hints[key] = true
        localStorage.setItem('sc_hints', JSON.stringify(hints))
      } catch (_) { /* localStorage unavailable */ }
    },
    updateMobileLayout() {
      if (typeof window === 'undefined') return
      this.isMobileLayout = window.innerWidth < 768
    },
    switchSearchMode(mode) {
      this.searchMode = mode
      this.barcode = ''
      this.manualSearch.query = ''
      this.manualSearch.results = []
      this.manualSearch.show = false
      this.manualSearch.activeIdx = -1
      this.manualSearch.focusTarget = 'input'
      try { localStorage.setItem('sc_search_mode', mode) } catch (_) { /* localStorage unavailable */ }
      this.focusSearch()
    },
    focusSearch() {
      if (this.isMobileLayout) return
      this.$nextTick(() => {
        if (this.searchMode === 'manual') {
          if (this.$refs.manualSearchInput) this.$refs.manualSearchInput.$el.focus()
        } else {
          if (this.$refs.barcodeInput) this.$refs.barcodeInput.$el.focus()
        }
      })
    },
    keepBarcodeFocus(e) {
      if (this.isMobileLayout || this.searchMode !== 'barcode' || this.product) return
      const barcodeEl = this.$refs.barcodeInput && this.$refs.barcodeInput.$el
      if (barcodeEl && e.target !== barcodeEl) barcodeEl.focus()
    },
    doManualSearch() {
      if (!this.manualSearch.query || this.manualSearch.query.length < 2) return
      this.manualSearch.loading = true
      this.manualSearch.show = true
      this.manualSearch.activeIdx = -1
      this.manualSearch.focusTarget = 'input'
      serviceAPI.buscarProductosPorTexto({ query: this.manualSearch.query })
        .then(response => {
          this.manualSearch.results = response.data
          this.manualSearch.activeIdx = -1
          this.manualSearch.focusTarget = 'input'
          this.manualSearch.show = true
          this.manualSearch.loading = false
        })
        .catch(() => { this.manualSearch.loading = false })
    },
    onDropdownItemTouchStart(e) {
      this._touchStartY = e.touches[0].clientY
      clearTimeout(this._manualBlurTimer)
    },
    onDropdownItemTouchEnd(item, e) {
      const dy = Math.abs(e.changedTouches[0].clientY - this._touchStartY)
      if (dy < 10) this.selectManualResult(item)
    },
    selectManualResult(item) {
      this.manualSearch.show = false
      this.loading.search = true
      this.searchError = null
      serviceAPI.buscarProductoPorId({ product_id: item.id })
        .then(response => {
          this.product = response.data.product
          this.unitMeasurement = response.data.unit_measurement
          this.unitMeasurements = response.data.unit_measurements || (this.unitMeasurement ? [this.unitMeasurement] : [])
          this.selectedUnitMeasurementId = this.getPreferredUnitMeasurementId()
          this.stockByDeposit = response.data.stock_by_deposit
          this.applySelectedUnitMeasurement()
          this.loading.search = false
        })
        .catch(error => {
          this.searchError = Error.showError(error)
          this.product = null
          this.stockByDeposit = []
          this.loading.search = false
        })
    },
    manualSearchMoveDown() {
      if (this.manualSearch.results.length === 0) return
      this.manualSearch.focusTarget = 'dropdown'
      this.manualSearch.activeIdx = Math.min(this.manualSearch.activeIdx + 1, this.manualSearch.results.length - 1)
      this.$nextTick(() => this.scrollActiveDropdownItem())
    },
    manualSearchMoveUp() {
      if (this.manualSearch.results.length === 0) return
      this.manualSearch.focusTarget = 'dropdown'
      this.manualSearch.activeIdx = Math.max(this.manualSearch.activeIdx - 1, 0)
      this.$nextTick(() => this.scrollActiveDropdownItem())
    },
    scrollActiveDropdownItem() {
      const container = this.$refs.manualDropdown
      if (!container) return
      const active = container.querySelector('.sc-manual-item--active')
      if (active) active.scrollIntoView({ block: 'nearest' })
    },
    manualSearchConfirm() {
      if (this.manualSearch.focusTarget !== 'dropdown') {
        clearTimeout(this.manualSearch._timer)
        this.doManualSearch()
        return
      }
      const idx = this.manualSearch.activeIdx
      if (idx >= 0 && this.manualSearch.results[idx]) {
        this.selectManualResult(this.manualSearch.results[idx])
      } else {
        clearTimeout(this.manualSearch._timer)
        this.doManualSearch()
      }
    },
    manualSearchFocus() {
      this.manualSearch.focusTarget = 'input'
      if (this.manualSearch.results.length > 0) {
        this.manualSearch.show = true
      }
    },
    manualSearchBlur() {
      this._manualBlurTimer = setTimeout(() => { this.manualSearch.show = false }, 200)
    },
    resetSearch() {
      this.barcode = ''
      this.manualSearch.query = ''
      this.manualSearch.results = []
      this.manualSearch.show = false
      this.manualSearch.activeIdx = -1
      this.manualSearch.focusTarget = 'input'
      this.product = null
      this.unitMeasurement = null
      this.unitMeasurements = []
      this.selectedUnitMeasurementId = null
      this.stockByDeposit = []
      this.searchError = null
      this.focusSearch()
    },
    searchProduct() {
      if (!this.barcode) return
      this.loading.search = true
      this.searchError = null

      serviceAPI.buscarPorCodigoBarras({ barcode: this.barcode })
        .then((response) => {
          this.product = response.data.product
          this.unitMeasurement = response.data.unit_measurement
          this.unitMeasurements = response.data.unit_measurements || (this.unitMeasurement ? [this.unitMeasurement] : [])
          this.selectedUnitMeasurementId = this.getPreferredUnitMeasurementId()
          this.stockByDeposit = response.data.stock_by_deposit
          this.applySelectedUnitMeasurement()
          this.loading.search = false
        })
        .catch((error) => {
          this.searchError = Error.showError(error)
          this.product = null
          this.stockByDeposit = []
          this.loading.search = false
        })
    },
    hasChanged(item) {
      return Math.abs((item.real_quantity || 0) - item.current_quantity) >= 0.00005
    },
    toNumber(value) {
      var number = parseFloat(value)
      return isNaN(number) ? 0 : number
    },
    onQuantityChange() {
      // reactive trigger for computed
    },
    getUnitMeasurementOptionText(item) {
      var reference = item.reference || item.name || 'Unidad'
      var type = item.type === 'sale' ? 'venta' : 'compra'
      return reference + ' - ' + type
    },
    getPreferredUnitMeasurementId() {
      var preferredType = Storage.getValue('deposits_stock_count_unit_type')
      var preferred = this.unitMeasurements.find(item => item.type === preferredType)
      if (preferred) return preferred.id
      return this.unitMeasurement ? this.unitMeasurement.id : null
    },
    onUnitMeasurementChange() {
      var selected = this.selectedUnitMeasurement
      if (selected && selected.type) {
        Storage.setValue('deposits_stock_count_unit_type', selected.type)
      }
      this.applySelectedUnitMeasurement()
    },
    applySelectedUnitMeasurement() {
      var selected = this.selectedUnitMeasurement
      if (!selected) return
      this.stockByDeposit = this.stockByDeposit.map(item => {
        var current = item.current_quantity
        if (selected.type === 'sale' && item.current_quantity_sale !== null && item.current_quantity_sale !== undefined) {
          current = item.current_quantity_sale
        }
        if (selected.type === 'purchase' && item.current_quantity_purchase !== null && item.current_quantity_purchase !== undefined) {
          current = item.current_quantity_purchase
        }
        current = this.normalizeQty(current)
        return Object.assign({}, item, {
          current_quantity: current,
          real_quantity: current,
        })
      })
    },
    getInputClass(item) {
      if (!this.hasChanged(item)) return ''
      return item.real_quantity > item.current_quantity ? 'border-success' : 'border-danger'
    },
    getDiffClass(item) {
      return item.real_quantity > item.current_quantity ? 'text-success' : 'text-danger'
    },
    async startCamera() {
      this.modal.camera = true
      await this.$nextTick()
      try {
        if ('BarcodeDetector' in window) {
          this.cameraEngine = 'native'
          this.cameraStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
          await this.attachCameraPreview(this.cameraStream)
          this.cameraDetector = new window.BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e']
          })
          this.cameraInterval = setInterval(this.detectBarcode, 400)
        } else {
          // iOS/Safari: sin BarcodeDetector se usa Quagga, que hace su propio
          // getUserMedia e inserta su propio <video> y <canvas> DENTRO del target,
          // por eso el target debe ser el div contenedor (nunca el <video>).
          this.cameraEngine = 'quagga'
          const Quagga = (await import('@ericblade/quagga2')).default
          await new Promise((resolve, reject) => {
            Quagga.init({
              inputStream: {
                type: 'LiveStream',
                target: this.$refs.cameraContainer,
                constraints: { facingMode: 'environment' },
              },
              decoder: { readers: ['ean_reader', 'ean_8_reader', 'code_128_reader', 'code_39_reader', 'upc_reader', 'upc_e_reader'] },
            }, (err) => { if (err) reject(err); else resolve() })
          })
          Quagga.start()
          this.quaggaInstance = Quagga
          this.ensureQuaggaVideoInline()
          Quagga.onDetected((data) => {
            const code = data && data.codeResult && data.codeResult.code
            if (code) {
              this.barcode = code
              this.stopCamera()
              this.modal.camera = false
              this.searchProduct()
            }
          })
        }
      } catch (e) {
        this.$bvToast.toast('No se pudo acceder a la cámara', { title: 'Error', variant: 'danger', solid: true })
        this.stopCamera()
        this.modal.camera = false
      }
    },
    // El video que inyecta Quagga necesita playsinline para que iOS no lo
    // bloquee (sin eso, Safari se niega a reproducirlo dentro de la página).
    ensureQuaggaVideoInline() {
      const container = this.$refs.cameraContainer
      if (!container) return
      const injected = Array.prototype.slice.call(container.querySelectorAll('video'))
        .find((v) => v !== this.$refs.videoElement)
      if (injected) {
        injected.setAttribute('playsinline', '')
        injected.setAttribute('webkit-playsinline', '')
        injected.setAttribute('muted', '')
        injected.muted = true
      }
    },
    async attachCameraPreview(stream) {
      const video = this.$refs.videoElement
      if (!video) return

      video.setAttribute('playsinline', '')
      video.setAttribute('webkit-playsinline', '')
      video.muted = true
      video.srcObject = stream

      if (video.readyState < 1) {
        await new Promise(resolve => {
          let resolved = false
          const done = () => {
            if (resolved) return
            resolved = true
            video.removeEventListener('loadedmetadata', done)
            resolve()
          }
          video.addEventListener('loadedmetadata', done, { once: true })
          setTimeout(done, 800)
        })
      }

      if (typeof video.play === 'function') {
        try {
          await video.play()
        } catch (e) {
          // Algunos navegadores bloquean play(); el stream sigue disponible para el detector.
        }
      }
    },
    async detectBarcode() {
      if (!this.$refs.videoElement || !this.cameraDetector) return
      if (this.$refs.videoElement.readyState < 2) return
      try {
        var barcodes = await this.cameraDetector.detect(this.$refs.videoElement)
        if (barcodes.length > 0) {
          this.barcode = barcodes[0].rawValue
          this.stopCamera()
          this.modal.camera = false
          this.searchProduct()
        }
      } catch (e) { /* ignorar frames no decodificables */ }
    },
    stopCamera() {
      clearInterval(this.cameraInterval)
      this.cameraInterval = null
      if (this.quaggaInstance) {
        this.quaggaInstance.offDetected()
        this.quaggaInstance.stop()
        this.quaggaInstance = null
      }
      // Saco el video/canvas que Quagga inyectó en el contenedor, para que
      // no se acumulen al volver a abrir el modal.
      const container = this.$refs.cameraContainer
      if (container) {
        Array.prototype.slice.call(container.children).forEach((child) => {
          if (child !== this.$refs.videoElement) container.removeChild(child)
        })
      }
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach(function(t) { t.stop() })
        this.cameraStream = null
      }
      this.cameraDetector = null
      this.cameraEngine = null
    },
    formatQty(val) {
      if (val === null || val === undefined) return '0'
      var n = this.normalizeQty(val)
      if (isNaN(n)) return '0'
      return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(n)
    },
    normalizeQty(val) {
      var n = parseFloat(val)
      if (isNaN(n)) return 0
      return parseFloat(n.toFixed(4))
    },
    confirmDialog(message, options) {
      var danger = !!options.danger
      var icon = options.icon || (danger ? 'warning' : 'help')
      var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
        this.$createElement('AppIcon', { props: { name: icon } }),
        options.title
      ])
      return this.$bvModal.msgBoxConfirm(message, {
        title: title,
        size: 'sm',
        buttonSize: 'sm',
        okVariant: danger ? 'danger' : 'dark',
        okTitle: options.okTitle,
        cancelTitle: options.cancelTitle || 'Cancelar',
        footerClass: 'p-2',
        headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
        hideHeaderClose: false,
        centered: true
      })
    },
    async processAdjustments() {
      if (this.changedCount === 0) return

      var unitReference = this.selectedUnitMeasurement && this.selectedUnitMeasurement.reference
        ? ' en ' + this.selectedUnitMeasurement.reference
        : ''
      var confirm = await this.confirmDialog('¿Desea procesar ' + this.changedCount + ' ajuste(s) de stock' + unitReference + '?', {
        title: 'Procesar ajustes',
        icon: 'check2-circle',
        okTitle: 'Procesar',
        cancelTitle: 'Cancelar'
      })

      if (!confirm) return

      this.loading.process = true

      var adjustments = this.stockByDeposit
        .filter(item => this.hasChanged(item))
        .map(item => ({
          deposit_id: item.deposit.id,
          real_quantity: item.real_quantity,
          current_quantity: item.current_quantity,
        }))

      serviceAPI.procesarAjustesStock({
        product_id: this.product.id,
        unit_measurement_id: this.selectedUnitMeasurement ? this.selectedUnitMeasurement.id : null,
        adjustments: adjustments,
        edit_source: 'stockCount',
      })
        .then((response) => {
          this.lastResult.operations_created = response.data.operations_created
          this.loading.process = false
          this.modal.success = true
        })
        .catch((error) => {
          this.$bvToast.toast(Error.showError(error), {
            title: 'Error al procesar',
            variant: 'danger',
            solid: true
          })
          this.loading.process = false
        })
    },
  }
}
</script>

<style scoped>
.camera-preview {
  background: var(--app-color-text);
  border-radius: 8px;
  max-height: 60vh;
  width: 100%;
}
.camera-preview-container {
  position: relative;
  background: var(--app-color-text);
  border-radius: 8px;
  overflow: hidden;
}
/* Video y canvas que inyecta Quagga (fallback en iOS/Safari) */
.camera-preview-container >>> video {
  display: block;
  width: 100%;
  max-height: 60vh;
  object-fit: contain;
}
.camera-preview-container >>> canvas.drawingBuffer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.stock-count-hint-close {
  color: var(--app-color-text-secondary);
  line-height: 1;
  flex-shrink: 0;
}
.stock-count-hint-close:hover { color: var(--app-color-text); }
.diff-label {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.97rem;
  font-weight: 500;
}
.sc-manual-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: var(--app-color-surface);
  border: 1px solid var(--app-color-border);
  border-radius: 0 0 0.375rem 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0,0,0,.15);
  max-height: 260px;
  overflow-y: auto;
}
.sc-manual-item {
  padding: 0.55rem 0.9rem;
  cursor: pointer;
  border-bottom: 1px solid var(--app-color-border);
}
.sc-manual-item:last-child { border-bottom: none; }
.sc-manual-item--active, .sc-manual-item:hover { background: var(--app-color-primary-soft); }
.sc-manual-item__name { font-size: 0.95rem; font-weight: 600; }
.sc-manual-item__meta { font-size: 0.78rem; color: var(--app-color-text-secondary); margin-top: 0.15rem; display: flex; flex-wrap: wrap; gap: 0.4rem; align-items: center; }
.sc-manual-item__code { background: var(--app-color-surface-subtle); padding: 0 0.3rem; border-radius: 3px; color: var(--app-color-text); font-size: 0.75rem; }
.sc-manual-item__cat { color: var(--app-color-text-secondary); }
.stock-count-page {
  min-height: 100vh;
  background: var(--app-color-canvas);
  font-size: 1.03rem;
}
.stock-count-header {
  background: var(--app-color-primary-900);
  z-index: 100;
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}
.stock-count-header h5 {
  font-size: 1.18rem;
}
.stock-count-header small {
  font-size: 0.84rem;
}
.stock-count-page .card {
  border-radius: 0.7rem;
}
.stock-count-page .card-body {
  padding: 1.05rem;
}
.stock-count-page .btn-lg {
  padding: 0.8rem 1.15rem;
  font-size: 1.02rem;
}
.stock-count-page .btn-sm {
  padding: 0.42rem 0.8rem;
  font-size: 0.9rem;
}
.stock-count-page .form-control,
.stock-count-page .custom-select,
.stock-count-page .input-group-text {
  min-height: 2.8rem;
  font-size: 1rem;
}
.stock-count-page .form-control-lg {
  min-height: 3.35rem;
  font-size: 1.02rem;
}
.stock-count-page .input-group > .input-group-prepend > .input-group-text,
.stock-count-page .input-group > .input-group-append > .input-group-text,
.stock-count-page .input-group > .input-group-append > .btn,
.stock-count-page .input-group > .input-group-prepend > .btn {
  min-height: 2.8rem;
}
.stock-count-page h6 {
  font-size: 0.98rem;
}
.stock-count-page .small {
  font-size: 0.88rem;
}
.stock-count-empty-state {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  border: 1px solid var(--app-color-primary-200);
  border-left: 0.35rem solid var(--app-color-primary);
  border-radius: 0.65rem;
  background: var(--app-color-primary-soft);
  color: var(--app-color-text);
  box-shadow: 0 0.125rem 0.35rem rgba(0, 0, 0, 0.06);
}
.stock-count-empty-icon {
  flex: 0 0 auto;
  font-size: 2rem;
  color: var(--app-color-primary);
}
.stock-count-empty-title {
  font-size: 1.12rem;
  font-weight: 700;
  line-height: 1.25;
}
.stock-count-empty-text {
  margin-top: 0.25rem;
  font-size: 0.98rem;
  line-height: 1.35;
  color: var(--app-color-text-secondary);
}
.stock-count-total-card {
  border: 1px solid var(--app-color-primary-200);
  border-left: 0.35rem solid var(--app-color-primary);
  border-radius: 0.7rem;
  background: var(--app-color-primary-soft);
  box-shadow: 0 0.15rem 0.45rem rgba(0, 0, 0, 0.08);
}
.stock-count-total-title {
  color: var(--app-color-text);
  font-size: 1.08rem;
  font-weight: 700;
  line-height: 1.2;
}
.stock-count-total-subtitle {
  color: var(--app-color-text-secondary);
  font-size: 0.88rem;
  margin-top: 0.2rem;
}
.stock-count-total-unit {
  color: var(--app-color-text);
  font-size: 0.86rem;
  padding: 0.35rem 0.55rem;
  border: 1px solid var(--app-color-primary-200);
}
.stock-count-total-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
.stock-count-total-box {
  padding: 0.85rem;
  border: 1px solid var(--app-color-border);
  border-radius: 0.55rem;
  background: var(--app-color-surface);
}
.stock-count-total-box-primary {
  border-color: var(--app-color-primary-200);
  background: var(--app-color-primary-soft);
}
.stock-count-total-label {
  color: var(--app-color-text-secondary);
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}
.stock-count-total-value {
  color: var(--app-color-text);
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1.15;
  margin-top: 0.2rem;
}
.stock-count-total-diff {
  margin-top: 0.8rem;
  font-size: 0.98rem;
  font-weight: 700;
}
.mobile-footer {
  left: 0;
  right: 0;
  background: var(--app-color-surface);
  z-index: 1000;
}

@media (min-width: 992px) {
  .sidebar-lg-show .mobile-footer {
    left: 200px;
    width: calc(100% - 200px);
  }
  .sidebar-lg-show.sidebar-minimized .mobile-footer {
    left: 50px;
    width: calc(100% - 50px);
  }
}
</style>

<style>
body.stock-count-focus-mode .app-header,
body.stock-count-focus-mode .sidebar,
body.stock-count-focus-mode .aside-menu,
body.stock-count-focus-mode .app-footer,
body.stock-count-focus-mode .alert-clear-cache,
body.stock-count-focus-mode .shortcuts-dropdown-full {
  display: none !important;
}

body.stock-count-focus-mode .app-body {
  margin-top: 0 !important;
}

body.stock-count-focus-mode .main {
  margin-left: 0 !important;
  width: 100% !important;
}

body.stock-count-focus-mode .main .container-fluid {
  padding-left: 0 !important;
  padding-right: 0 !important;
  padding-top: 0 !important;
}
</style>
